import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  useTheme
} from '@mui/material';
import { fetchDailyStepUsage } from '../../api/billing';
import DailyBarChart from '../../components/charts/DailyBarChart';
import { buildMonthOptions, nowInPT } from '../../utils/BillingHelpers';

const CREDITS_COLOR_KEY = 'credits';

const CreditsUsedChartCard: React.FC = () => {
  const theme = useTheme();
  const monthOptions = useMemo(buildMonthOptions, []);

  const current = nowInPT();
  const currentKey = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;
  const availableKeys = new Set(monthOptions.map((m) => m.key));
  const defaultKey = availableKeys.has(currentKey)
    ? currentKey
    : monthOptions[0]?.key;

  const [dailyUsage, setDailyUsage] = useState<
    Array<{ date: string; credits: number }>
  >([]);
  const [chartLoading, setChartLoading] = useState(true);
  const [selectedMonthKey, setSelectedMonthKey] = useState<string>(defaultKey);

  const selectedMonth = useMemo(() => {
    return (
      monthOptions.find((m) => m.key === selectedMonthKey) ?? monthOptions[0]
    );
  }, [selectedMonthKey, monthOptions]);

  const colorMap = useMemo(
    () => ({ [CREDITS_COLOR_KEY]: theme.customColors.themePrimary }),
    [theme]
  );

  useEffect(() => {
    const fetchData = async () => {
      setChartLoading(true);
      try {
        const { year, monthIdx } = selectedMonth;
        const month1Based = monthIdx + 1;
        const usageRes = await fetchDailyStepUsage(month1Based, year);
        setDailyUsage(
          (usageRes?.daily_usage || []).map(({ date, steps }) => ({
            date: date.split('T')[0],
            credits: steps
          }))
        );
      } catch (err) {
        console.error('Error fetching daily usage:', err);
        setDailyUsage([]);
      } finally {
        setChartLoading(false);
      }
    };

    fetchData();
  }, [selectedMonth]);

  return (
    <DailyBarChart
      chartTitle="Credits used per day"
      data={dailyUsage}
      colorMap={colorMap}
      year={selectedMonth.year}
      monthIdx={selectedMonth.monthIdx}
      headerAction={
        <FormControl
          size="small"
          sx={{
            minWidth: 220,
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: theme.customColors.themePrimary
              },
              '&:hover fieldset': {
                borderColor: `${theme.customColors.themePrimary}80`
              }
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: theme.customColors.themePrimary
            },
            '& .MuiSvgIcon-root': {
              color: theme.customColors.themePrimary
            }
          }}
        >
          <InputLabel id="usage-month-select-label">Month</InputLabel>
          <Select
            labelId="usage-month-select-label"
            label="Month"
            value={selectedMonthKey}
            onChange={(e) => setSelectedMonthKey(e.target.value)}
            disabled={chartLoading}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 360,
                  overflowY: 'auto',
                  '& .MuiMenuItem-root.Mui-selected': {
                    backgroundColor: `${theme.customColors.themePrimary}20`,
                    '&:hover': {
                      backgroundColor: `${theme.customColors.themePrimary}30`
                    }
                  }
                }
              },
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              transformOrigin: { vertical: 'top', horizontal: 'left' },
              disableScrollLock: true
            }}
          >
            {monthOptions.map((opt) => (
              <MenuItem key={opt.key} value={opt.key}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      }
      loading={chartLoading}
    />
  );
};

export default CreditsUsedChartCard;
